import React, { useEffect, useState, useCallback, useMemo } from "react";
import Loader from "../../../../../../components/Loader";
import { useTheme } from "../../../../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { KeyboardArrowDownRounded, KeyboardArrowUpRounded, DataSaverOff, StackedBarChart } from "@mui/icons-material";
import CallQueueTable from "../CallQueueTable";
import ChartsWebex from "../../../Charts/ChartsWebex";

export default function TypeCallQueue({ data, allCalls, flatCalls, callQueue, setDataGraph, setFormattedHeader, user }) {
    const { theme } = useTheme();
    const [groupSurvey, setGroupSurvey] = useState();
    const [transformedData, setTransformedData] = useState();
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [paginationPerGroup, setPaginationPerGroup] = useState({});
    const [dataTypeTrends, setDataTypeTrends] = useState([]);
    const [selectedTypeChart, setSelectedTypeChart] = useState('chart-round');
    const itemsPerPage = 15;
    const { t } = useTranslation();

    // Optimisation : Map inversée pour trouver le NOM rapidement (et pour le tri)
    const reverseCallQueue = useMemo(() => {
        const map = {};
        if (callQueue) {
            Object.entries(callQueue).forEach(([name, number]) => {
                map[number] = name;
            });
        }
        return map;
    }, [callQueue]);

    useEffect(() => {
        if (data) {
            handleGraphGroupSurvey(data);
            handleGraphGroupSurveyTrends(data);
            const resetPagination = {};
            Object.keys(data).forEach((groupIndex) => {
                resetPagination[groupIndex] = 1;
            });
            setPaginationPerGroup(prev => {
                if (JSON.stringify(prev) !== JSON.stringify(resetPagination)) {
                    return resetPagination;
                }
                return prev;
            });
        }
    }, [data, allCalls, flatCalls, callQueue]);

    useEffect(() => {
        if (groupSurvey) {
            setTransformedData(transformData(groupSurvey))
        }
    }, [groupSurvey]);

    // Tri alphabétique pour l'affichage (A -> Z par Nom)
    const sortedGroupKeys = useMemo(() => {
        if (!groupSurvey) return [];
        return Object.keys(groupSurvey).sort((a, b) => {
            const nameA = reverseCallQueue[a] || "";
            const nameB = reverseCallQueue[b] || "";
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
    }, [groupSurvey, reverseCallQueue]);

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const isPositiveAnswerIndicator = (answerIndicator) => (
        answerIndicator === "Yes" || answerIndicator === "Yes-PostRedirection"
    );

    const isQueueDistributionCall = (call) => (
        call?.calltype === "SIP_ENTERPRISE"
        && (call?.redirectreason === "CallQueue" || call?.relatedreason === "CallQueue")
    );

    const isAnsweredUserCall = (call) => (
        (
            (call?.usertype === "User" && call?.direction === "TERMINATING")
            || isQueueDistributionCall(call)
        )
        && isPositiveAnswerIndicator(call?.answerindicator)
        && String(call?.answered) === "1"
    );

    const collectCallsFromNode = (node, correlationid) => {
        if (!node) return [];

        if (Array.isArray(node)) {
            return node.flatMap(item => collectCallsFromNode(item, correlationid));
        }

        const calls = Array.isArray(node.calls)
            ? node.calls.map(call => ({ ...call, correlationid: call?.correlationid || correlationid }))
            : [];

        const childrenCalls = node.children
            ? Object.values(node.children).flatMap(child => collectCallsFromNode(child, correlationid))
            : [];

        const legCalls = node.legs
            ? Object.values(node.legs).flatMap(leg => collectCallsFromNode(leg, node.correlationid || correlationid))
            : [];

        const currentCall = node.correlationid && !node.legs && !node.calls
            ? [{ ...node }]
            : [];

        return [...calls, ...childrenCalls, ...legCalls, ...currentCall];
    };

    const getAllCallsByCorrelationId = (correlationid) => {
        const matchingFlatCalls = Array.isArray(flatCalls)
            ? flatCalls.filter(call => call?.correlationid === correlationid)
            : [];

        if (!Array.isArray(allCalls)) return matchingFlatCalls;

        const matchingTreeCalls = allCalls.flatMap(item => {
            const itemCorrelationId = item?.correlationid || item?.correlationId;

            if (item?.legs && itemCorrelationId === correlationid) {
                return collectCallsFromNode(item, itemCorrelationId);
            }

            if (itemCorrelationId === correlationid) {
                return [item];
            }

            return [];
        });

        return [...matchingFlatCalls, ...matchingTreeCalls];
    };

    const getQueueAnswerIndicator = (queueCall, correlationCalls) => {
        const completeCorrelationCalls = [
            ...(Array.isArray(correlationCalls) ? correlationCalls : []),
            ...getAllCallsByCorrelationId(queueCall?.correlationid)
        ];

        const hasAnsweredUser = completeCorrelationCalls
            .some(call => call?.correlationid === queueCall?.correlationid && isAnsweredUserCall(call));

        if (hasAnsweredUser) {
            return isPositiveAnswerIndicator(queueCall?.answerindicator) ? queueCall.answerindicator : "Yes";
        }

        return "No";
    };

    const handleGraphGroupSurvey = (data) => {
        let callqueueStats = {};
        
        // --- MODIFICATION 1 : Création d'un Set avec les ID des groupements valides ---
        // On récupère uniquement les valeurs (numéros) de l'objet callQueue
        const validGroupIds = new Set(Object.values(callQueue || {}));

        data.map(item => {
            if (Array.isArray(item.calls)) {
                return item.calls.filter(call => {
                    const group = call.callednumber;

                    // --- MODIFICATION 2 : Vérification stricte ---
                    // Si pas de numéro OU si ce numéro n'est pas dans la liste des groupements officiels, on l'ignore.
                    if (!group || !validGroupIds.has(group)) return false;

                    if (!callqueueStats[group]) {
                        callqueueStats[group] = {
                            Incoming: {
                                total: 0,
                                durationTotal: 0,
                                averageduration: 0,
                                response: 0,
                                averageresponse: 0,
                                noresponse: 0,
                                ringduration: 0,
                                averageringduration: 0
                            },
                            calls: []
                        };
                    }

                    if (call.direction === "TERMINATING") {
                        const answerParentIndicator = getQueueAnswerIndicator(call, item.calls);
                        const callWithAnswerParentIndicator = {
                            ...call,
                            answerParentIndicator
                        };

                        callqueueStats[group].Incoming.total += 1;
                        callqueueStats[group].Incoming.durationTotal += Math.floor(parseInt(call.duration) || 0);
                        callqueueStats[group].Incoming.ringduration += Math.floor(parseInt(call.ringduration) || 0);
                        
                        if (isPositiveAnswerIndicator(answerParentIndicator)) {
                            callqueueStats[group].Incoming.response += 1;
                            callqueueStats[group].Incoming.averageresponse += Math.floor(parseInt(call.ringduration) || 0);
                        } else if (answerParentIndicator === "No") {
                            callqueueStats[group].Incoming.noresponse += 1;
                        }
                        callqueueStats[group].calls.push(callWithAnswerParentIndicator);
                    }
                });
            }
            return [];
        });

        for (let group in callqueueStats) {
            // Suppression des groupes vides (si vous souhaitez aussi masquer ceux qui n'ont eu aucun appel)
            if (callqueueStats[group].Incoming.total === 0) {
                delete callqueueStats[group];
                continue;
            }

            let Incoming = callqueueStats[group].Incoming;

            Incoming.averageduration = Incoming.total > 0 ? Incoming.durationTotal / Incoming.total : 0;
            Incoming.averageresponse = Incoming.response > 0 ? Incoming.averageresponse / Incoming.response : 0;
            Incoming.averageringduration = Incoming.ringduration > 0 ? Incoming.response + Incoming.noresponse / Incoming.ringduration : 0;

            Incoming.durationTotal = formatDuration(Incoming.durationTotal);
            Incoming.averageduration = formatDuration(Incoming.averageduration);
            Incoming.averageresponse = formatDuration(Incoming.averageresponse);
            Incoming.ringduration = formatDuration(Incoming.ringduration);
            Incoming.averageringduration = formatDuration(Incoming.averageringduration);
        }

        if (JSON.stringify(callqueueStats) !== JSON.stringify(groupSurvey)) {
            setGroupSurvey(callqueueStats);
        }
        const formattedData = formatForExcel(callqueueStats);
        setDataGraph(formattedData);
    }

    const handleGraphGroupSurveyTrends = (data) => {
        // Pour les trends aussi, on pourrait filtrer, mais les données sont agrégées.
        // Idéalement on utilise la même logique, mais ici cela impacterait moins l'affichage visuel tableau.
        // Si besoin d'être cohérent, on peut ajouter le filtre ici aussi :
        const validGroupIds = new Set(Object.values(callQueue || {}));

        const aggregatedResults = Array.from({ length: 24 }, (_, hour) => ({
            key: `${hour}:00`,
            date: "",
            Incoming: {
                total: 0,
                durationTotal: 0,
                averageduration: 0,
                response: 0,
                averageresponse: 0,
                noresponse: 0,
            },
        }));
    
        const callsByHourAndDate = {
            responded: {},
            notResponded: {},
        };
    
        data.forEach(item => {
            if (Array.isArray(item.calls)) {
                item.calls.forEach(call => {
                    // Ajout du filtre de sécurité ici aussi pour le graphique
                    if (call.callednumber && validGroupIds.has(call.callednumber) && call.direction === "TERMINATING") {
                        const date = new Date(call.starttime);
                        const hour = date.getHours();
                        const dateString = date.toLocaleDateString();
                        const duration = Math.floor(parseInt(call.duration) || 0);
                        const ringDuration = Math.floor(parseInt(call.ringduration) || 0);
                        const isResponse = isPositiveAnswerIndicator(getQueueAnswerIndicator(call, item.calls));
    
                        if (!callsByHourAndDate.responded[hour]) callsByHourAndDate.responded[hour] = {};
                        if (!callsByHourAndDate.notResponded[hour]) callsByHourAndDate.notResponded[hour] = {};
    
                        if (isResponse) {
                            if (!callsByHourAndDate.responded[hour][dateString]) callsByHourAndDate.responded[hour][dateString] = [];
                            callsByHourAndDate.responded[hour][dateString].push({ call, duration, ringDuration });
                        } else {
                            if (!callsByHourAndDate.notResponded[hour][dateString]) callsByHourAndDate.notResponded[hour][dateString] = [];
                            callsByHourAndDate.notResponded[hour][dateString].push({ call, duration, ringDuration });
                        }
                    }
                });
            }
        });
    
        const processCalls = (callType) => {
            Object.keys(callsByHourAndDate[callType]).forEach(hour => {
                const dateCounts = callsByHourAndDate[callType][hour];
                let maxCallsDate = "";
                let maxCallsCount = 0;
    
                Object.keys(dateCounts).forEach(date => {
                    const numCalls = dateCounts[date].length;
                    if (numCalls > maxCallsCount) {
                        maxCallsCount = numCalls;
                        maxCallsDate = date;
                    }
                });
    
                const hourlyEntry = aggregatedResults[hour];
                if (!hourlyEntry.date) hourlyEntry.date = maxCallsDate;
    
                const callsForMaxDate = dateCounts[maxCallsDate];
                if (callsForMaxDate && Array.isArray(callsForMaxDate)) {
                    callsForMaxDate.forEach(({ duration, ringDuration }) => {
                        hourlyEntry.Incoming.total += 1;
                        hourlyEntry.Incoming.durationTotal += duration;
                        if (callType === "responded") {
                            hourlyEntry.Incoming.response += 1;
                            hourlyEntry.Incoming.averageresponse += ringDuration;
                        } else if (callType === "notResponded") {
                            hourlyEntry.Incoming.noresponse += 1;
                        }
                    });
                }
            });
        };
    
        processCalls("responded");
        processCalls("notResponded");
    
        aggregatedResults.forEach(hourData => {
            const data = hourData.Incoming;
            data.averageduration = data.total > 0 ? data.durationTotal / data.total : 0;
            data.averageresponse = data.response > 0 ? data.averageresponse / data.response : 0;
            data.durationTotal = formatDuration(data.durationTotal);
            data.averageduration = formatDuration(data.averageduration);
            data.averageresponse = formatDuration(data.averageresponse);
        });
    
        setDataTypeTrends(aggregatedResults);
    };    

    const formatForExcel = (data) => {
        const formattedData = [];

        // Tri pour l'export Excel (A-Z)
        const sortedKeys = Object.keys(data).sort((a, b) => {
            const nameA = Object.keys(callQueue).find(key => callQueue[key] === a) || "";
            const nameB = Object.keys(callQueue).find(key => callQueue[key] === b) || "";
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });

        sortedKeys.forEach((groupKey) => {
            const groupData = data[groupKey];
            formattedData.push([`${Object.keys(callQueue).find(key => callQueue[key] === groupKey)} : ${groupKey}`, "", "", "", "", ""]);

            formattedData.push([
                "Nombre d'appels répondu",
                groupData.Incoming.response || 0,
                ""
            ]);

            formattedData.push([
                "Durée moyenne pour répondre",
                groupData.Incoming.averageresponse || "00:00:00",
                ""
            ]);

            formattedData.push([
                "Nombre d'appels non répondu",
                groupData.Incoming.noresponse || 0,
                ""
            ]);

            formattedData.push([
                "Durée totale d'appels",
                groupData.Incoming.durationTotal || "00:00:00",
                ""
            ]);

            formattedData.push([
                "Durée moyenne d'appels",
                groupData.Incoming.averageduration || "00:00:00",
                ""
            ]);

            formattedData.push([
                "Durée totale de d'attente",
                groupData.Incoming.ringduration || "00:00:00",
                ""
            ]);

            formattedData.push([
                "Durée moyenne de d'attente",
                groupData.Incoming.averageringduration || "00:00:00",
                ""
            ]);

            formattedData.push([]);
        });
        setFormattedHeader(true);
        return formattedData;
    };

    const transformData = (data) => {
        const result = {};
        for (const index in data) {
            const nameQueue = Object.keys(callQueue).find(key => callQueue[key] === index);
            result[nameQueue] = [data[index].Incoming.total, index];
        }
        return result;
    };

    const handleThClick = () => {
        setExpandedRows(prevRows => {
            if (prevRows.size === 0) {
                const allRows = new Set(Object.keys(groupSurvey).map((_, index) => index));
                return allRows;
            } else {
                return new Set();
            }
        });
    };

    const toggleRowExpansion = useCallback((groupIndex) => {
        setExpandedRows(prevRows => {
            const newRows = new Set(prevRows);
            if (newRows.has(groupIndex)) {
                newRows.delete(groupIndex);
            } else {
                newRows.add(groupIndex);
                if (!paginationPerGroup[groupIndex]) {
                    setPaginationPerGroup(prev => (prev[groupIndex] ? prev : { ...prev, [groupIndex]: 1 }));
                }
            }
            return newRows;
        });
    }, [paginationPerGroup]);

    const handleTypeChartChange = (newTypeChart) => {
        setSelectedTypeChart(newTypeChart);
    };

    return (
        <div className="callQueueGroupSurvey">
            {groupSurvey && transformedData && expandedRows && paginationPerGroup ? (
                <>
                    {transformedData && (
                        <div className="containerChartsCallQueue">
                            <div className="callQueueGroupSurveyCharts">
                                <h2 className="page-chart-webex-title">{selectedTypeChart === 'chart-round' ?  t("Nombre total d'appels par groupement") : t("Comment varient les appels dans le temps")}</h2>
                                <div className="page-chart-webex-total-content-buttons">
                                    <div className="typeTrends-element-charts">
                                        <div className="page-chart-webex-total-buttons">
                                            <button className={`${selectedTypeChart === 'chart-round' ? 'button-target-data' : ''}`} onClick={() => handleTypeChartChange('chart-round')}><DataSaverOff /></button>
                                            <button className={`${selectedTypeChart === 'chart-bar' ? 'button-target-data' : ''}`} onClick={() => handleTypeChartChange('chart-bar')}><StackedBarChart /></button>
                                        </div>
                                    </div>
                                </div>
                                {selectedTypeChart === "chart-round" ? (
                                    <ChartsWebex
                                        key={theme}
                                        dataForChart={transformedData}
                                        allLabels={Object.keys(transformedData)}
                                        typeOfChart={"doughnut"}
                                        labelTitle={transformedData}
                                        theme={theme}
                                    />
                                ) : (
                                    <div>
                                        {Object.values(
                                            dataTypeTrends.reduce((acc, item) => {
                                                acc[item.key] = item.Incoming.response;
                                                return acc;
                                            }, {})
                                        ).some(value => value !== 0) && (
                                                <div>
                                                    <ChartsWebex
                                                        key={theme}
                                                        dataForChart={dataTypeTrends.reduce((acc, item) => {
                                                            acc[item.key] = item.Incoming.response;
                                                            return acc;
                                                        }, {})}
                                                        allLabels={Object.values(dataTypeTrends).map(item => item.key)}
                                                        allDates={Object.values(dataTypeTrends).map(item => item.date)}
                                                        typeOfChart={"trends-bar"}
                                                        titleChart={t("Entrant - Nombre d'appels répondus")}
                                                        theme={theme}
                                                    />
                                                </div>
                                            )}
                                        {Object.values(
                                            dataTypeTrends.reduce((acc, item) => {
                                                acc[item.key] = item.Incoming.noresponse;
                                                return acc;
                                            }, {})
                                        ).some(value => value !== 0) && (
                                                <div>
                                                    <ChartsWebex
                                                        key={theme}
                                                        dataForChart={dataTypeTrends.reduce((acc, item) => {
                                                            acc[item.key] = item.Incoming.noresponse;
                                                            return acc;
                                                        }, {})}
                                                        allLabels={Object.values(dataTypeTrends).map(item => item.key)}
                                                        allDates={Object.values(dataTypeTrends).map(item => item.date)}
                                                        typeOfChart={"trends-bar"}
                                                        titleChart={t("Entrant - Nombre d'appels non répondus")}
                                                        theme={theme}
                                                    />
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="table-callqueue">
                        <table className="callQueueGroupSurvey-entrants">
                            <thead>
                                <tr>
                                    <th className="thclickexpanded"></th>
                                    <th title={t("Groupe")}>{t("Groupe")}</th>
                                    <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                    <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                    <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                    <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                    <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                                    <th title={t("Durée totale d'attente")}>{t("Durée totale d'attente")}</th>
                                    <th title={t("Durée moyenne d'attente")}>{t("Durée moyenne d'attente")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* sortedGroupKeys contient maintenant uniquement les vrais groupements, non vides et triés */}
                                {sortedGroupKeys.map((group, index) => (
                                    <React.Fragment key={index}>
                                        <tr key={index}>
                                            <td className='statistics-table-wrapper-content-element'>
                                                <div className='legsCallingOpen' onClick={() => toggleRowExpansion(group)}>
                                                    {expandedRows.has(group) ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                                                </div>
                                            </td>
                                            <td>{reverseCallQueue[group] || group}</td>
                                            <td>{groupSurvey[group].Incoming.response}</td>
                                            <td>{groupSurvey[group].Incoming.averageresponse}</td>
                                            <td>{groupSurvey[group].Incoming.noresponse}</td>
                                            <td>{groupSurvey[group].Incoming.durationTotal}</td>
                                            <td>{groupSurvey[group].Incoming.averageduration}</td>
                                            <td>{groupSurvey[group].Incoming.ringduration}</td>
                                            <td>{groupSurvey[group].Incoming.averageringduration}</td>
                                        </tr>
                                        {expandedRows.has(group) && (
                                            <tr key={`expanded-row-${index}`}>
                                                <td colSpan={9} className="container-list-post-callqueue">
                                                    <CallQueueTable groupSurvey={groupSurvey} allCalls={allCalls} group={group} index={index} itemsPerPage={itemsPerPage} paginationPerGroup={paginationPerGroup} setPaginationPerGroup={setPaginationPerGroup} user={user} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                <Loader />
            )}
        </div>
    );
}
